import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Text } from '@chakra-ui/react'
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from '../../../ui/dialog'
import { colors } from '../../../../utilities/colors'

export default function AddressConfirmPrompt({ isOpen, label, enteredLocation, quoteLocation, suggestion, onUseSuggested, onUseEntered, onEdit }) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState('suggested')

  useEffect(() => {
    if (isOpen) setSelected(suggestion ? 'suggested' : 'entered')
  }, [isOpen, suggestion])

  const formatEntered = () => {
    if (!enteredLocation) return ''
    const street = [enteredLocation.address, enteredLocation.address_2].filter(Boolean).join(', ')
    const stateZip = [quoteLocation?.state, quoteLocation?.postal_code].filter(Boolean).join(' ')
    const cityLine = [quoteLocation?.city, stateZip].filter(Boolean).join(', ')
    return [street, cityLine].filter(Boolean).join(', ')
  }

  const handleContinue = () => {
    if (selected === 'suggested') onUseSuggested()
    else onUseEntered()
  }

  const tileStyles = (isSelected) => ({
    as: 'button',
    type: 'button',
    textAlign: 'left',
    w: 'full',
    p: 3,
    borderRadius: 'md',
    border: '2px solid',
    borderColor: isSelected ? colors.green : colors.grey700,
    bg: isSelected ? colors.grey700 : colors.grey900,
    cursor: 'pointer',
    _hover: { borderColor: isSelected ? colors.green : colors.grey300 },
  })

  return (
    <DialogRoot
      role="alertdialog"
      open={isOpen}
      onOpenChange={(details) => { if (!details.open) onEdit?.() }}
    >
      <DialogTrigger />
      <DialogContent bg={colors.deepOcean} width={{ base: 'calc(100% - 32px)', md: 'auto' }} mx="auto" maxW="md">
        <DialogTitle>
          <div className="text-lg font-bold m-2">
            {suggestion ? t('Confirm {{label}} Address', { label }) : t("We Couldn't Verify This Address")}
          </div>
        </DialogTitle>
        <DialogBody>
          <Box display="flex" flexDirection="column" gap={3} mx={2}>
            <Text fontSize="sm" color={colors.grey300}>
              {suggestion
                ? t('We found a slightly different address. Pick the one you want to use.')
                : t("We couldn't find this address. You can continue with what you entered or go back and edit it.")}
            </Text>

            {suggestion && (
              <Box {...tileStyles(selected === 'suggested')} onClick={() => setSelected('suggested')}>
                <Text fontSize="xs" color={selected === 'suggested' ? colors.green : colors.grey300} fontWeight="semibold" mb={1}>
                  {t('Suggested')}
                </Text>
                <Text fontSize="sm" color={colors.grey100}>
                  {suggestion.formattedAddress || suggestion.address}
                </Text>
              </Box>
            )}

            <Box {...tileStyles(selected === 'entered')} onClick={() => setSelected('entered')}>
              <Text fontSize="xs" color={selected === 'entered' ? colors.green : colors.grey300} fontWeight="semibold" mb={1}>
                {t('Use as entered')}
              </Text>
              <Text fontSize="sm" color={colors.grey100}>
                {formatEntered()}
              </Text>
            </Box>
          </Box>
        </DialogBody>
        <DialogFooter>
          <Button onClick={onEdit} borderRadius="full" px={5} py={2} variant="outline" borderColor={colors.grey300} color={colors.grey100}>
            {t('Edit')}
          </Button>
          <Button onClick={handleContinue} ml={2} px={5} py={2} borderRadius="full" bg={colors.green} color={colors.grey100}>
            {t('Continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
